// Durable Sherloc generation operations — client (Cortex) contract module.
//
// Backend contract (frozen):
//   POST /api/sherloc/media-planner/operations
//     body: same payload as the legacy sync POST /api/sherloc/media-planner
//     plus an optional clientRequestId.
//     201 {ok, operation, reused:false} — new operation queued
//     200 {ok, operation, reused:true} — reconnected to an in-flight duplicate
//     200 {ok, operation, completedReplay:true} — identical inputs already
//         completed; operation.result is the legacy sync success envelope and
//         must be applied WITHOUT regenerating.
//     409 {code:'operation_in_progress', operation} — reconnect, don't error
//     409 {code:'operation_terminally_rejected', operation, overrideField} —
//         TERMINAL: no operation was created and no provider call was made.
//         Adopt `operation` as the terminal state; never leave the surface
//         polling or showing an in-progress label.
//     400 {code:'selected_platforms_required'}
//   GET  /api/sherloc/media-planner/operations/:id            → {ok, operation}
//   GET  /api/sherloc/media-planner/operations?campaignId=X&active=1
//                                                             → {ok, operation|null}
//   POST /api/sherloc/media-planner/operations/:id/retry      → 202
//     409 operation_failed_terminal | operation_not_retryable
//   POST /api/sherloc/media-planner/operations/:id/cancel
//
// operation: {id, operationType, campaignId, status, attemptCount, retryable,
//   failureClass, failureMessage, strategyPlanVersion, approvalRequired,
//   createdAt, startedAt, lastHeartbeatAt, completedAt, failedAt, elapsedMs,
//   result?}  — result only when COMPLETED.
//
// There is NO client-side generation timeout in the durable path: generation
// continues on the server, and the page polls (pausing while hidden).

export const SHERLOC_GENERATION_OPERATIONS_PATH =
  "/api/sherloc/media-planner/operations";

// 4s polling cadence (paused while document.hidden; an immediate poll runs on
// focus/visibility restore).
export const SHERLOC_GENERATION_POLL_INTERVAL_MS = 4000;

export const SHERLOC_GENERATION_OPERATION_STATUSES = Object.freeze({
  QUEUED: "QUEUED",
  GENERATING: "GENERATING",
  VALIDATING: "VALIDATING",
  PERSISTING: "PERSISTING",
  COMPLETED: "COMPLETED",
  FAILED_RETRYABLE: "FAILED_RETRYABLE",
  FAILED_TERMINAL: "FAILED_TERMINAL",
  SUPERSEDED: "SUPERSEDED",
  CANCELLED: "CANCELLED",
});

const STATUSES = SHERLOC_GENERATION_OPERATION_STATUSES;

export const SHERLOC_GENERATION_ACTIVE_STATUSES = Object.freeze([
  STATUSES.QUEUED,
  STATUSES.GENERATING,
  STATUSES.VALIDATING,
  STATUSES.PERSISTING,
]);

// Every status renders an explicit labeled state — the strategy panel must
// never be unlabeled-empty while an operation exists.
export const SHERLOC_GENERATION_STAGE_LABELS = Object.freeze({
  [STATUSES.QUEUED]: "Queued",
  [STATUSES.GENERATING]: "Sherloc is generating the strategy",
  [STATUSES.VALIDATING]: "Reviewing for accuracy",
  [STATUSES.PERSISTING]: "Saving plan",
  [STATUSES.COMPLETED]: "Strategy ready",
  [STATUSES.FAILED_RETRYABLE]: "Generation failed — safe to retry",
  [STATUSES.FAILED_TERMINAL]: "Generation could not complete",
  [STATUSES.SUPERSEDED]: "Superseded by a newer generation",
  [STATUSES.CANCELLED]: "Generation cancelled",
});

export const SHERLOC_GENERATION_BACKGROUND_NOTE =
  "You can leave or reload this page — generation continues on the server.";

const normalizeStatus = (value) => String(value ?? "").trim().toUpperCase();

export const getSherlocGenerationOperationStatus = (operation) =>
  normalizeStatus(operation?.status);

export const isSherlocGenerationOperationActive = (operation) =>
  SHERLOC_GENERATION_ACTIVE_STATUSES.includes(
    getSherlocGenerationOperationStatus(operation)
  );

export const isSherlocGenerationOperationCompleted = (operation) =>
  getSherlocGenerationOperationStatus(operation) === STATUSES.COMPLETED;

export const isSherlocGenerationOperationFailed = (operation) =>
  [STATUSES.FAILED_RETRYABLE, STATUSES.FAILED_TERMINAL].includes(
    getSherlocGenerationOperationStatus(operation)
  );

export const isSherlocGenerationOperationRetryable = (operation) =>
  getSherlocGenerationOperationStatus(operation) === STATUSES.FAILED_RETRYABLE;

export const getSherlocGenerationOperationStageLabel = (operation) => {
  const status = getSherlocGenerationOperationStatus(operation);
  if (!status) return "Generation status unavailable";
  return SHERLOC_GENERATION_STAGE_LABELS[status] || `Generation status: ${status}`;
};

// ---------------------------------------------------------------------------
// Elapsed / relative-time presentation
// ---------------------------------------------------------------------------

const parseTimestampMs = (value) => {
  const parsed = Date.parse(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : null;
};

// Server-computed elapsedMs is authoritative at receipt; while the operation is
// still active the display keeps counting from the moment the snapshot was
// received (receivedAtMs) without trusting client/server clock alignment.
export const getSherlocGenerationOperationElapsedMs = (
  operation,
  { nowMs = Date.now(), receivedAtMs = null } = {}
) => {
  if (!operation) return 0;
  const base = Number(operation.elapsedMs);
  const settled = Number.isFinite(base) ? Math.max(0, base) : 0;
  if (!isSherlocGenerationOperationActive(operation)) return settled;
  const anchor = Number.isFinite(Number(receivedAtMs)) ? Number(receivedAtMs) : nowMs;
  return settled + Math.max(0, nowMs - anchor);
};

export const formatSherlocGenerationElapsedSeconds = (elapsedMs) => {
  const totalSeconds = Math.max(0, Math.floor(Number(elapsedMs || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, "0")}s` : `${seconds}s`;
};

export const getSherlocGenerationOperationLastUpdateAt = (operation) =>
  operation?.lastHeartbeatAt ||
  operation?.completedAt ||
  operation?.failedAt ||
  operation?.startedAt ||
  operation?.createdAt ||
  null;

export const formatSherlocGenerationRelativeTime = (isoValue, nowMs = Date.now()) => {
  const timestamp = parseTimestampMs(isoValue);
  if (timestamp === null) return "no update yet";
  const deltaSeconds = Math.max(0, Math.floor((nowMs - timestamp) / 1000));
  if (deltaSeconds < 5) return "just now";
  if (deltaSeconds < 60) return `${deltaSeconds}s ago`;
  const minutes = Math.floor(deltaSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

// ---------------------------------------------------------------------------
// Duplicate-click guard: exactly one durable POST per user intent.
// ---------------------------------------------------------------------------

export const acquireSherlocGenerationSubmitLatch = (latchRef, activeOperation) => {
  if (!latchRef) return false;
  if (latchRef.current === true) return false;
  if (isSherlocGenerationOperationActive(activeOperation)) return false;
  latchRef.current = true;
  return true;
};

export const releaseSherlocGenerationSubmitLatch = (latchRef) => {
  if (latchRef) latchRef.current = false;
};

// ---------------------------------------------------------------------------
// Poll outcome resolution (pure): what a poll snapshot means for the page.
// ---------------------------------------------------------------------------

export const resolveSherlocGenerationPollOutcome = (operation) => {
  const status = getSherlocGenerationOperationStatus(operation);
  if (!status || isSherlocGenerationOperationActive(operation)) {
    return { kind: "continue" };
  }
  if (status === STATUSES.COMPLETED) {
    // Apply the completed result through the existing success handling —
    // never start a new generation for an already-completed operation.
    return operation?.result
      ? { kind: "apply", result: operation.result }
      : { kind: "completed_without_result" };
  }
  return { kind: "terminal", status };
};

// Pause polling while the document is hidden; visibility/focus listeners run
// an immediate poll on wake.
export const shouldPauseSherlocGenerationPolling = (doc = typeof document !== "undefined" ? document : null) =>
  Boolean(doc && doc.hidden === true);

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

const trimBase = (base) => String(base || "").replace(/\/+$/, "");

const readJsonPayload = async (response) => {
  const raw = await response.text().catch(() => "");
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return { __malformed: true };
  }
};

const buildOperationRequestError = (response, payload) => {
  const error = new Error(
    String(payload?.error || payload?.message || "").trim() ||
      `Sherloc generation operation request failed (${response.status}).`
  );
  error.status = response.status;
  error.code = String(
    payload?.code || payload?.errorCode || payload?.error_code || ""
  ).trim();
  error.backendCode = error.code || null;
  error.missingFields = Array.isArray(payload?.missingFields)
    ? payload.missingFields
    : null;
  // 409 operation_in_progress (and retry refusals) carry the current
  // operation so the caller can reconnect instead of failing.
  error.operation = payload?.operation || null;
  error.payload = payload;
  return error;
};

const requestSherlocGenerationOperationApi = async ({
  apiBase,
  token,
  path,
  method = "GET",
  requestId = "",
  body = null,
  fetchImpl,
}) => {
  const doFetch = fetchImpl || fetch;
  const response = await doFetch(`${trimBase(apiBase)}${path}`, {
    method,
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(requestId ? { "X-Request-Id": requestId } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await readJsonPayload(response);
  if (!response.ok) throw buildOperationRequestError(response, payload);
  return payload;
};

// POST / — create or reuse a durable generation operation. The body is the
// exact legacy sync payload plus clientRequestId.
export const createSherlocGenerationOperation = ({
  apiBase,
  token,
  requestId,
  body,
  fetchImpl,
}) =>
  requestSherlocGenerationOperationApi({
    apiBase,
    token,
    requestId,
    path: SHERLOC_GENERATION_OPERATIONS_PATH,
    method: "POST",
    body,
    fetchImpl,
  });

// GET /:id — poll one operation.
export const getSherlocGenerationOperation = ({
  apiBase,
  token,
  operationId,
  requestId,
  fetchImpl,
}) =>
  requestSherlocGenerationOperationApi({
    apiBase,
    token,
    requestId,
    path: `${SHERLOC_GENERATION_OPERATIONS_PATH}/${encodeURIComponent(operationId)}`,
    fetchImpl,
  });

// GET /?campaignId=X[&active=1] — reconnect after reload. active=1 returns the
// in-flight operation or null; without it, the latest operation of any status.
export const getSherlocGenerationOperationForCampaign = ({
  apiBase,
  token,
  campaignId,
  activeOnly = true,
  requestId,
  fetchImpl,
}) => {
  const params = new URLSearchParams();
  params.set("campaignId", String(campaignId));
  if (activeOnly) params.set("active", "1");
  return requestSherlocGenerationOperationApi({
    apiBase,
    token,
    requestId,
    path: `${SHERLOC_GENERATION_OPERATIONS_PATH}?${params.toString()}`,
    fetchImpl,
  });
};

// POST /:id/retry — resume a FAILED_RETRYABLE operation (202 on accept).
export const retrySherlocGenerationOperation = ({
  apiBase,
  token,
  operationId,
  requestId,
  fetchImpl,
}) =>
  requestSherlocGenerationOperationApi({
    apiBase,
    token,
    requestId,
    path: `${SHERLOC_GENERATION_OPERATIONS_PATH}/${encodeURIComponent(operationId)}/retry`,
    method: "POST",
    fetchImpl,
  });

// ---------------------------------------------------------------------------
// Submission orchestration (pure wiring, behavior-testable without React):
// acquire the latch, run exactly one POST, and classify the acknowledgement.
// ---------------------------------------------------------------------------

export const startSherlocDurableGeneration = async ({
  latchRef,
  activeOperation = null,
  submit,
}) => {
  if (typeof submit !== "function") {
    throw new TypeError("startSherlocDurableGeneration requires a submit function");
  }
  if (!acquireSherlocGenerationSubmitLatch(latchRef, activeOperation)) {
    return { kind: "duplicate_suppressed" };
  }
  try {
    const payload = await submit();
    const operation = payload?.operation || null;
    if (
      payload?.completedReplay === true &&
      isSherlocGenerationOperationCompleted(operation) &&
      operation?.result
    ) {
      // Identical inputs already completed — apply without regenerating.
      return { kind: "completed_replay", operation, result: operation.result };
    }
    if (isSherlocGenerationOperationCompleted(operation) && operation?.result) {
      return { kind: "completed_replay", operation, result: operation.result };
    }
    if (operation) {
      return { kind: "track", operation, reused: payload?.reused === true };
    }
    return { kind: "unacknowledged" };
  } catch (error) {
    if (error?.code === "operation_in_progress" && error?.operation) {
      // A generation is already running for this campaign — reconnect to it.
      return { kind: "track", operation: error.operation, reused: true };
    }
    // These exact inputs already failed governance terminally. The refusal is
    // a TERMINAL ANSWER, not a transport error: the backend deliberately made
    // no provider call and created no operation row, and it hands back the
    // prior rejected operation so the caller can show which attempt failed.
    //
    // Falling through to `throw` left the surface with no operation to poll
    // and no terminal state to adopt, so the campaign row stayed on its
    // last optimistic label forever. Production, campaign 50: request
    // smp_mszfkjpt_q78w47 (2026-08-19T01:46:24Z) was refused against op
    // 2dc718bb and the row sat in UPDATING… indefinitely.
    if (error?.code === "operation_terminally_rejected" && error?.operation) {
      return {
        kind: "terminal_rejection",
        operation: error.operation,
        message: error.message || null,
        overrideField: error.overrideField || "allowTerminallyRejectedInputs",
      };
    }
    throw error;
  } finally {
    releaseSherlocGenerationSubmitLatch(latchRef);
  }
};
